/**
 * Frontpage warm-up: the projects grid first, then the newest news teasers.
 *
 * PHP (library/preload-projects.php) emits both image sets as JSON —
 * srcset/sizes strings straight from glunz_image_srcset_data(), so the
 * browser natively picks the exact candidate the target page will request
 * (same viewport, same DPR), and the long-lived cache headers on
 * uploads/image-cache/ keep it cached across the navigation. Everything runs
 * through the shared warmQueue: projects in the PROJECT bucket, news behind
 * them in the casual one — the site-wide rule is project imagery first.
 *
 * Projects are skipped on Chromium: the Speculation Rules prerender
 * (library/preload-projects.php) already pulls the whole projects page —
 * Chromium users who disabled preloading (Battery/Data Saver, settings) get
 * neither, deliberately: they opted out of speculative traffic. News warm on
 * EVERY engine — /aktuell/ is only prerendered on tap, and a warm HTTP cache
 * is exactly what makes that prerender paint instantly. Deferred while the
 * frontpage is itself only being prerendered (hovering any home link
 * prerenders it), after load + idle; skipped on Save-Data / slow connections.
 */
import { fastConnection } from './utils'
import { warmImage } from './warmQueue'

const dataScript = document.querySelector('[data-projects-preload]')
const supportsSpeculation = HTMLScriptElement.supports?.('speculationrules')

if (dataScript && fastConnection()) {
  const warm = () => {
    let data
    try {
      data = JSON.parse(dataScript.textContent)
    } catch {
      return
    }
    if (!supportsSpeculation) {
      for (const item of data.projects ?? []) warmImage(item, 'project')
    }
    for (const item of data.news ?? []) warmImage(item)
  }

  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300))
  const start = () => idle(warm)
  const whenLoaded = () => {
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
  }

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', whenLoaded, { once: true })
  } else {
    whenLoaded()
  }
}
